import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { shallowEqualObjects } from 'shallow-equal';

import { useSafeLayoutEffect, useStable } from '@gitbook/util-react-hooks';

import {
    deKeyVariantsValue,
    getDefaultVariantsValue,
    type KeyedVariantsValue,
    keyVariantsValue,
    Variants,
    type VariantsValue,
} from './variants';

interface ThemeConfigurationState {
    system: KeyedVariantsValue;
    app: KeyedVariantsValue | undefined;
}

const defaultConfigurationState: ThemeConfigurationState = {
    system: keyVariantsValue({
        ...getDefaultVariantsValue(),
        color: getSystemColorScheme() || Variants.color.default,
    }),
    app: undefined,
};

/**
 * Global atom to store the current theme and the one defined in the system.
 * Using an atom instead of a context to ensure changing the app theme applies to all elements.
 */
const configurationThemeAtom = atom<ThemeConfigurationState>(defaultConfigurationState);

/**
 * Storage key holding the user's theme preference. The application owns the value and is the only
 * writer; this package only reads it, to paint the right theme before `ConfigurationTheme` mounts
 * (on the login/signup screens it never does).
 */
export const USER_THEME_STORAGE_KEY = 'userThemeAtom';

/**
 * Read-only access to the theme configuration.
 * We use a context because reading the atom directly would make every consumer subscribe to it.
 */
export const ThemeConfigurationContext =
    React.createContext<ThemeConfigurationState>(defaultConfigurationState);

/**
 * Provider for the theming configuration.
 */
export function ThemeConfigurationProvider(props: {
    children: React.ReactNode;
}): React.ReactElement {
    const { children } = props;
    const configurationValue = useAtomValue(configurationThemeAtom);
    const setConfiguration = useSetAtom(configurationThemeAtom);
    const storedThemeColor = React.useMemo(() => readStoredThemeColor(), []);
    const value = React.useMemo(() => {
        if (configurationValue.app || !storedThemeColor) {
            return configurationValue;
        }
        return {
            ...configurationValue,
            app: keyVariantsValue({
                ...getDefaultVariantsValue(),
                color: storedThemeColor,
            }),
        };
    }, [configurationValue, storedThemeColor]);

    React.useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const callback = () => {
            setConfiguration((previous) => ({
                ...previous,
                system: keyVariantsValue({
                    ...getDefaultVariantsValue(),
                    color: getSystemColorScheme() || Variants.color.default,
                }),
            }));
        };

        mediaQuery.addEventListener('change', callback);
        return () => mediaQuery.removeEventListener('change', callback);
    }, [setConfiguration]);

    // Handle the change of the color scheme in the browser when we don't have an app theme
    const handlePrefersColorSchemeChange = React.useCallback(
        (e: MediaQueryList | MediaQueryListEvent) => {
            const colorScheme = e.matches ? 'dark' : 'light';

            // Apply the correct theme classes to the body element
            Object.entries(Variants).forEach(([key, { values }]) => {
                values.forEach((value) => {
                    const className = `theme-${key}-${value}`;
                    document.documentElement.classList.toggle(className, value === colorScheme);
                });
            });
        },
        []
    );

    // Indicate to the browser which color-scheme as been choosen in the app
    // to ensure browser UI elements are correctly rendered.
    React.useEffect(() => {
        if (value.app) {
            // @ts-ignore 'colorScheme' does exist in modern browsers
            document.documentElement.style.colorScheme = deKeyVariantsValue(value.app).color;
        } else {
            // Set the initial color scheme when we don't have an app theme
            // Necessary for login/signup page, if we don't set the initial color scheme, tailwind can't apply the correct classes.
            if (typeof window.matchMedia !== 'function') {
                return;
            }

            const darkThemeMq = window.matchMedia('(prefers-color-scheme: dark)');
            handlePrefersColorSchemeChange(darkThemeMq);

            darkThemeMq.addEventListener('change', handlePrefersColorSchemeChange);

            return () => {
                darkThemeMq.removeEventListener('change', handlePrefersColorSchemeChange);
            };
        }
    }, [value, handlePrefersColorSchemeChange]);

    return (
        <ThemeConfigurationContext.Provider value={value}>
            {children}
        </ThemeConfigurationContext.Provider>
    );
}

/**
 * Define the palette/theme for the UI
 */
export function ConfigurationTheme(props: {
    theme: Partial<VariantsValue>;
    children: React.ReactNode;
}): React.ReactElement {
    const { children } = props;
    const [state, setState] = useAtom(configurationThemeAtom);
    const theme = useStable(props.theme, shallowEqualObjects);

    React.useEffect(() => {
        Object.entries(Variants).forEach(([key, { values }]) => {
            values.forEach((value) => {
                const className = `theme-${key}-${value}`;
                document.documentElement.classList.toggle(className, theme[key] === value);
            });
        });
    }, [theme]);

    const keyedVariantsValue = React.useMemo(() => {
        return keyVariantsValue({
            ...(state.app ? deKeyVariantsValue(state.app) : getDefaultVariantsValue()),
            ...theme,
        });
    }, [state.app, theme]);

    /**
     * It's important that we `setState` in an effect only when the theme value has changed.
     * Because `ConfigurationTheme` can be used by multiple components, we don't want to end up
     * in an infinite loop where those components are forcing a re-renders due to different
     * values of `keyedVariantsValue` (their theme)
     */
    useSafeLayoutEffect(() => {
        setState((prevValue) => {
            if (prevValue.app !== keyedVariantsValue) {
                return { ...prevValue, app: keyedVariantsValue };
            }
            return prevValue;
        });
    }, [keyedVariantsValue, setState]);

    return <>{children}</>;
}

function readStoredThemeColor(): 'dark' | 'light' | null {
    if (typeof window === 'undefined') {
        return null;
    }

    let value: string | null = null;
    try {
        value = window.localStorage.getItem(USER_THEME_STORAGE_KEY);
    } catch {
        // localStorage can be unavailable in privacy-restricted contexts.
        return null;
    }

    if (!value || (!value.includes('dark') && !value.includes('light'))) {
        return null;
    }
    return value.includes('dark') ? 'dark' : 'light';
}

function getSystemColorScheme(): 'dark' | 'light' | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
